import { FC } from "react";
import { Command } from "@vendor/cmdk/cmdk";
import { pluralizeWithCount } from "@common/helpers/string";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { CommandsDirectory, CommandsListProps } from "./lib";
import { useCommands } from "./useCommands";

export const InvoiceBulkCommandsList: FC<CommandsListProps> = ({
    options = {},
}) => {
    const { open } = useCommands();
    const {
        selectedInvoiceCount = 0,
        onInvoiceBulkMarkVoid,
        onInvoiceBulkMarkUncollectible,
        invoiceBulkCounterpartyOptions,
        invoiceBulkScheduleOptions,
        canInvoiceBulkChangeCounterparty,
        onInvoiceBulkDownloadPdf,
        canInvoiceBulkDownloadPdf,
        invoiceBulkDownloadPdfDisabledTooltip,
        onInvoiceBulkDelete,
        canInvoiceBulkDelete,
        onInvoiceBulkCreateTask,
    } = options;

    const heading = `${pluralizeWithCount("invoice", selectedInvoiceCount)} selected`;

    return (
        <Command.Group heading={heading}>
            {onInvoiceBulkMarkVoid && (
                <CommandItem
                    keywords={["void", "mark void", "cancel"]}
                    onSelect={onInvoiceBulkMarkVoid}
                >
                    Mark void
                </CommandItem>
            )}
            {onInvoiceBulkMarkUncollectible && (
                <CommandItem
                    keywords={[
                        "uncollectible",
                        "mark uncollectible",
                        "bad debt",
                    ]}
                    onSelect={onInvoiceBulkMarkUncollectible}
                >
                    Mark uncollectible
                </CommandItem>
            )}
            {invoiceBulkCounterpartyOptions &&
                invoiceBulkCounterpartyOptions.length > 0 && (
                    <CommandItem
                        keywords={[
                            "counterparty",
                            "vendor",
                            "customer",
                            "payee",
                        ]}
                        disabled={!canInvoiceBulkChangeCounterparty}
                        disabledTooltip="Only available when every selected invoice is a draft"
                        onSelect={() =>
                            open(
                                CommandsDirectory.INVOICE_BULK_COUNTERPARTY,
                                options,
                            )
                        }
                        shortcut="v"
                    >
                        Set counterparty
                    </CommandItem>
                )}
            {invoiceBulkScheduleOptions &&
                invoiceBulkScheduleOptions.length > 0 && (
                    <CommandItem
                        keywords={["schedule", "deferred", "recognition"]}
                        onSelect={() =>
                            open(
                                CommandsDirectory.INVOICE_BULK_SCHEDULE,
                                options,
                            )
                        }
                    >
                        Set recognition schedule
                    </CommandItem>
                )}
            {onInvoiceBulkDownloadPdf && (
                <CommandItem
                    keywords={["download", "pdf", "export"]}
                    disabled={!canInvoiceBulkDownloadPdf}
                    disabledTooltip={
                        invoiceBulkDownloadPdfDisabledTooltip ??
                        "Only invoices created in Kick can be downloaded as a PDF"
                    }
                    onSelect={onInvoiceBulkDownloadPdf}
                >
                    Download PDF
                </CommandItem>
            )}
            {onInvoiceBulkCreateTask && (
                <CommandItem
                    keywords={["task", "create"]}
                    onSelect={onInvoiceBulkCreateTask}
                    shortcut="t"
                >
                    Create task
                </CommandItem>
            )}
            {onInvoiceBulkDelete && (
                <CommandItem
                    keywords={["delete", "remove", "draft"]}
                    disabled={!canInvoiceBulkDelete}
                    disabledTooltip="Only manually created, imported, or MCP draft invoices can be deleted"
                    onSelect={onInvoiceBulkDelete}
                >
                    Delete drafts
                </CommandItem>
            )}
        </Command.Group>
    );
};
